import { Injectable, inject } from '@angular/core';
import { Country } from '@appTypes/api.types';
import { appLanguageChanged } from '@app-state/app.actions';
import { ApiErrorResponse } from '@core/interceptors/api.interceptor';
import { DataLoadStatusModel, RequestStateModel } from '@core/store/store.models';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { AppPersistedState } from '@app-state/app.persisted.state';
import { CatalogueApiService } from '@shared/services/api/catalogue-api.service';
import { EMPTY, catchError, switchMap, tap } from 'rxjs';
import {
  CruiseApiError,
  CruiseBookResult,
  CruiseCheck,
  CruiseDraft,
  CruiseGuest,
  CruiseLive,
  CruiseSailing,
  CruiseSearchOptions,
  CruiseSearchRequest,
  CruiseSearchResult
} from '../cruises.types';
import { guestWithoutGender, priceSignature } from '../cruises.utils';
import { CruisesApiService } from '../services/cruises-api.service';
import {
  cruisesAcceptPriceChange,
  cruisesAddGuest,
  cruisesBook,
  cruisesCheckGuests,
  cruisesClearSearchForm,
  cruisesDeclinePriceChange,
  cruisesGetLivePrice,
  cruisesGoTo,
  cruisesLoadCountries,
  cruisesLoadSearchOptions,
  cruisesOpenSailing,
  cruisesReloadSailing,
  cruisesRemoveGuest,
  cruisesReset,
  cruisesSearch,
  cruisesSelectOffer,
  cruisesSetCabin,
  cruisesSetDining,
  cruisesSetPackage,
  cruisesSetSearchForm,
  cruisesUpdateGuest
} from './cruises.actions';
import {
  CruiseWizardDraft,
  CruisesStateModel,
  emptyCruiseSearchForm,
  normalizeCruiseSearchForm
} from './cruises.model';

type Ctx = StateContext<CruisesStateModel>;

/** A failed call as a request state the templates read: code, Trace ID and the whole CruiseApiError (args, the kept reservation). */
export const cruiseErrorState = <T>(err: CruiseApiError): RequestStateModel<T> =>
  new RequestStateModel<T>(
    new DataLoadStatusModel(
      'error',
      err.message,
      new ApiErrorResponse<CruiseApiError>({
        errorCode: err.code,
        activityId: err.traceId ?? undefined,
        displayErrorMessage: err.message,
        error: err
      })
    )
  );

const loading = <T>(): RequestStateModel<T> => new RequestStateModel<T>(new DataLoadStatusModel('loading'));

/** R-120a: a search adult (seq 1..adults) the draft holds with neither an age nor a birth date - the options had not answered at the search. */
const lacksAdultAge = (draft: CruiseWizardDraft, adults: number): boolean =>
  draft.guests.some((g) => g.seq <= adults && g.age === null && !g.birthDate);

/** R-120a: every search adult without age and birth date gets the options' adult age; guests added on W4 (seq > adults) are left as they are. */
const withAdultAge = (draft: CruiseWizardDraft, adults: number, adultAge: number): CruiseWizardDraft => ({
  ...draft,
  guests: draft.guests.map((g) => (g.seq <= adults && g.age === null && !g.birthDate ? { ...g, age: adultAge } : g))
});

const newGuest = (seq: number, birthDate: string | null, age: number | null = null): CruiseGuest => ({
  seq,
  lastName: null,
  firstName: null,
  birthDate,
  citizenship: null,
  passportNumber: null,
  passportExpires: null,
  passportIssued: null,
  passportIssuePlace: null,
  loyaltyNumber: null,
  withBothParents: null,
  authorisationConfirmed: null,
  isMale: null,
  rftid: null,
  age
});

/** The draft as online-api takes it (plan Task 10 WebsiteDraft). */
export const toApiDraft = (d: CruiseWizardDraft): CruiseDraft => ({
  channelId: d.channelId,
  sailingId: d.sailingId,
  offerKey: d.offerKey ?? '',
  cabinNumber: d.cabinNumber,
  diningKey: d.diningKey,
  guests: d.guests,
  packages: d.packages
});

const signatureOf = (d: CruiseWizardDraft): string => priceSignature(d.offerKey, d.guests, d.packages);

/**
 * Cruises phase 4 - the Cruises tab of the agency website (W1-W6), the Excursions page's NGXS pattern. Search → sailing → live price →
 * cabin → guests → packages → check → register → quote / option (R-54). The server decides every rule (the live answer carries the reasons,
 * C3); the state only keeps what the agency sees and never shows a price as live that was not asked for these guests (R-3, R-57).
 * R-115 / R-120: W1 searches with adults + the children's ages; the draft's guests start from them (an age each, no birth date) until W4.
 * QA doc: docs/features/cruises/qa.md R-1 … R-6, R-47 … R-57, R-115 … R-120.
 */
@State<CruisesStateModel>({ name: 'cruises', defaults: new CruisesStateModel() })
@Injectable()
export class CruisesState {
  readonly #api = inject(CruisesApiService);
  readonly #catalogue = inject(CatalogueApiService);
  readonly #store = inject(Store);

  /** a form restored from session storage may be of the older shape - always a complete one (R-119) */
  @Selector() static searchForm({ searchForm }: CruisesStateModel) {
    return normalizeCruiseSearchForm(searchForm);
  }
  @Selector() static searchOptions({ searchOptions }: CruisesStateModel) {
    return searchOptions;
  }
  @Selector() static searchResults({ searchResults }: CruisesStateModel) {
    return searchResults;
  }
  @Selector() static step({ step }: CruisesStateModel) {
    return step;
  }
  @Selector() static hit({ hit }: CruisesStateModel) {
    return hit;
  }
  @Selector() static sailing({ sailing }: CruisesStateModel) {
    return sailing;
  }
  @Selector() static draft({ draft }: CruisesStateModel) {
    return draft;
  }
  @Selector() static shown({ shown }: CruisesStateModel) {
    return shown;
  }
  @Selector() static live({ live }: CruisesStateModel) {
    return live;
  }
  /** R-3: the live answer counts only for the offer, guests and packages it was asked for. */
  @Selector() static liveIsCurrent({ live, liveSignature, draft }: CruisesStateModel): boolean {
    return !!(draft && live.response && liveSignature && liveSignature === signatureOf(draft));
  }
  /** what the live answer was asked for (the reprice banner's "before") */
  @Selector() static liveSignature({ liveSignature }: CruisesStateModel) {
    return liveSignature;
  }
  @Selector() static priceChange({ priceChange }: CruisesStateModel) {
    return priceChange;
  }
  @Selector() static guestCheck({ guestCheck }: CruisesStateModel) {
    return guestCheck;
  }
  @Selector() static booking({ booking }: CruisesStateModel) {
    return booking;
  }
  @Selector() static booked({ booked }: CruisesStateModel) {
    return booked;
  }
  @Selector() static countries({ countries }: CruisesStateModel) {
    return countries;
  }

  @Action(cruisesReset)
  reset({ setState }: Ctx) {
    setState(new CruisesStateModel());
  }

  @Action(cruisesLoadSearchOptions, { cancelUncompleted: true })
  loadSearchOptions({ getState, patchState }: Ctx, { refresh }: cruisesLoadSearchOptions) {
    if (getState().searchOptions.loadState.status === 'completed' && !refresh) return;
    patchState({ searchOptions: loading<CruiseSearchOptions>() });
    return this.#api.searchOptions().pipe(
      tap((options) => patchState({ searchOptions: new RequestStateModel<CruiseSearchOptions>(options) })),
      catchError((err: CruiseApiError) => {
        patchState({ searchOptions: cruiseErrorState<CruiseSearchOptions>(err) });
        return EMPTY;
      })
    );
  }

  @Action(cruisesSetSearchForm)
  setSearchForm({ getState, patchState }: Ctx, { patch }: cruisesSetSearchForm) {
    patchState({ searchForm: { ...normalizeCruiseSearchForm(getState().searchForm), ...patch } });
  }

  @Action(cruisesClearSearchForm)
  clearSearchForm({ patchState }: Ctx) {
    patchState({ searchForm: emptyCruiseSearchForm() });
  }

  /** R-115 / R-117: one search per click, for the adults + children's ages and the lists of the form (the form refuses a missing date or age first). */
  @Action(cruisesSearch, { cancelUncompleted: true })
  search({ getState, patchState }: Ctx) {
    const form = normalizeCruiseSearchForm(getState().searchForm);
    if (!form.from || !form.to || form.childAges.some((a) => a === null)) return;
    const childAges = form.childAges.filter((a): a is number => a !== null);
    const request: CruiseSearchRequest = {
      departureFrom: form.from,
      departureTo: form.to,
      nightsFrom: form.nightsFrom,
      nightsTo: form.nightsTo,
      adults: form.adults,
      childAges,
      countryIds: form.countryId ? [form.countryId] : [],
      embarkPortIds: form.portIds,
      cruiseLineIds: form.brandIds,
      shipIds: form.shipIds,
      cabinTypeIds: form.cabinTypeIds,
      fareFamilyIds: form.fareFamilyIds,
      onlyAvailable: form.onlyAvailable,
      regionIds: form.regionIds,
      russianSpeakingService: form.russianSpeakingOnly ? true : null,
      cruiseTypeIds: form.cruiseTypeIds,
      priceMin: form.priceMin,
      priceMax: form.priceMax,
      sort: form.sort,
      // R-129 (docs#450): the header currency - From / To and the price sort compare in it (the Hotels / Tours read of the same selector)
      currency: this.#store.selectSnapshot(AppPersistedState.activedCurrency) ?? null
    };
    patchState({
      searchResults: loading<CruiseSearchResult>(),
      searchGuests: { adults: form.adults, childAges, adultAge: getState().searchOptions.response?.adultAge ?? null },
      searchRequest: request,
      step: 'w1'
    });
    return this.#api.search(request).pipe(
      tap((result) =>
        patchState({ searchResults: new RequestStateModel<CruiseSearchResult>(result ?? { sailings: [] }) })
      ),
      catchError((err: CruiseApiError) => {
        patchState({ searchResults: cruiseErrorState<CruiseSearchResult>(err) });
        return EMPTY;
      })
    );
  }

  /**
   * R-56 (website-web#153): brands, ships, ports and cabin types come from cruises-service in the request's language (Accept-Language), so a
   * language switch asks again what is on the screen - the filter lists, the last search (same request, the step and the page stay) and the
   * open sailing. The live price is a supplier call and is not asked again. Dispatched after the new language is in the state (R-CK9b).
   */
  @Action(appLanguageChanged, { cancelUncompleted: true })
  languageChanged({ getState, patchState, dispatch }: Ctx) {
    const { searchOptions, searchRequest, searchResults, hit, sailing, countries } = getState();
    if (searchOptions.loadState.status === 'completed') dispatch(new cruisesLoadSearchOptions(true));
    if (countries.loadState.status === 'completed') dispatch(new cruisesLoadCountries(true));
    if (hit && sailing.loadState.status === 'completed') dispatch(new cruisesReloadSailing(true));
    if (!searchRequest || searchResults.loadState.status !== 'completed') return;
    return this.#api.search(searchRequest).pipe(
      tap((result) => {
        // a Search pressed while this re-ask ran owns the results - the late answer for the old request is dropped
        if (getState().searchRequest !== searchRequest) return;
        const sailings = result?.sailings ?? [];
        const current = getState().hit;
        const same =
          current && sailings.find((s) => s.channelId === current.channelId && s.sailingId === current.sailingId);
        patchState({
          searchResults: new RequestStateModel<CruiseSearchResult>(result ?? { sailings: [] }),
          ...(same ? { hit: same } : {})
        });
      }),
      // silent (R-56a): a failed re-ask keeps the results on the screen in the earlier language
      catchError(() => EMPTY)
    );
  }

  @Action(cruisesOpenSailing)
  openSailing({ getState, patchState, dispatch }: Ctx, { hit }: cruisesOpenSailing) {
    // R-120: the adults first (the options' adultAge), then the children in their order (their ages) - no birth date until W4
    const searched = getState().searchGuests;
    // R-120a: options that answered after the search still give the adults their age
    const adultAge = searched?.adultAge ?? getState().searchOptions.response?.adultAge ?? null;
    const guests = searched
      ? [
          ...Array.from({ length: searched.adults }, (_, i) => newGuest(i + 1, null, adultAge)),
          ...searched.childAges.map((age, i) => newGuest(searched.adults + i + 1, null, age))
        ]
      : [];
    patchState({
      step: 'w2',
      hit,
      draft: {
        channelId: hit.channelId,
        sailingId: hit.sailingId,
        offerKey: null,
        cabinNumber: null,
        diningKey: null,
        guests,
        packages: [],
        rfid: null,
        rfNumber: null,
        quoteId: null,
        rowVersion: null
      },
      shown: null,
      live: new RequestStateModel<CruiseLive>(),
      liveSignature: null,
      priceChange: null,
      guestCheck: new RequestStateModel<CruiseCheck>(),
      booking: new RequestStateModel<CruiseBookResult>(),
      booked: null
    });
    return dispatch(new cruisesReloadSailing());
  }

  @Action(cruisesReloadSailing, { cancelUncompleted: true })
  reloadSailing({ getState, patchState }: Ctx, { silent }: cruisesReloadSailing) {
    const { hit, searchGuests, searchRequest } = getState();
    if (!hit || !searchGuests) return;
    if (!silent) patchState({ sailing: loading<CruiseSailing>() });
    return this.#api
      .sailing({
        channelId: hit.channelId,
        sailingId: hit.sailingId,
        adults: searchGuests.adults,
        childAges: searchGuests.childAges,
        // R-123a (core/cruises-service#22): the grid shows what the search showed - "Cabins available" off keeps the on-request fares
        onlyAvailable: searchRequest?.onlyAvailable ?? true
      })
      .pipe(
        tap((sailing) => patchState({ sailing: new RequestStateModel<CruiseSailing>(sailing) })),
        catchError((err: CruiseApiError) => {
          // R-56a: a silent re-ask (language switch) keeps the sailing the agent is working on
          if (!silent) patchState({ sailing: cruiseErrorState<CruiseSailing>(err) });
          return EMPTY;
        })
      );
  }

  @Action(cruisesSelectOffer)
  selectOffer({ getState, patchState }: Ctx, { offerKey }: cruisesSelectOffer) {
    const { draft, sailing } = getState();
    const cell = sailing.response?.matrix.find((c) => c.offerKey === offerKey);
    if (!draft || !cell) return;
    const next: CruiseWizardDraft = { ...draft, offerKey, cabinNumber: null, diningKey: null, packages: [] };
    patchState({
      draft: next,
      shown: { signature: signatureOf(next), total: cell.total, currency: cell.currency },
      live: new RequestStateModel<CruiseLive>(),
      liveSignature: null,
      guestCheck: new RequestStateModel<CruiseCheck>()
    });
  }

  @Action(cruisesGetLivePrice, { cancelUncompleted: true })
  getLivePrice({ getState, patchState }: Ctx, { compare }: cruisesGetLivePrice) {
    const { draft, searchGuests } = getState();
    if (!draft?.offerKey) return;
    const adults = searchGuests?.adults ?? 0;
    if (!lacksAdultAge(draft, adults)) return this.#askLivePrice(getState, patchState, compare);
    // R-120a: never ask the supplier for a search adult with no age and no birth date (CRUISE_GUEST_DOB_REQUIRED) - fill it from the options first
    const known = getState().searchOptions.response?.adultAge;
    if (known !== null && known !== undefined) {
      this.#fillAdultAge(getState, patchState, draft, adults, known);
      return this.#askLivePrice(getState, patchState, compare);
    }
    patchState({ live: loading<CruiseLive>() });
    return this.#api.searchOptions().pipe(
      switchMap((options) => {
        patchState({ searchOptions: new RequestStateModel<CruiseSearchOptions>(options) });
        const current = getState().draft;
        if (!current || options?.adultAge === null || options?.adultAge === undefined) {
          patchState({ live: new RequestStateModel<CruiseLive>(new DataLoadStatusModel(null)), liveSignature: null });
          return EMPTY;
        }
        this.#fillAdultAge(getState, patchState, current, adults, options.adultAge);
        return this.#askLivePrice(getState, patchState, compare) ?? EMPTY;
      }),
      catchError((err: CruiseApiError) => {
        // R-57: no price labelled live; the box shows the options call's code and Trace ID
        patchState({ live: cruiseErrorState<CruiseLive>(err), liveSignature: null });
        return EMPTY;
      })
    );
  }

  /** R-120a: the adults get the age; the catalogue price on screen was shown for the same guests, so it keeps counting as "shown" (R-3 compares with it). */
  #fillAdultAge(
    getState: Ctx['getState'],
    patchState: Ctx['patchState'],
    draft: CruiseWizardDraft,
    adults: number,
    age: number
  ) {
    const next = withAdultAge(draft, adults, age);
    const { shown } = getState();
    patchState({
      draft: next,
      shown: shown && shown.signature === signatureOf(draft) ? { ...shown, signature: signatureOf(next) } : shown
    });
  }

  #askLivePrice(getState: Ctx['getState'], patchState: Ctx['patchState'], compare: boolean) {
    const { draft } = getState();
    if (!draft?.offerKey) return;
    const signature = signatureOf(draft);
    const before = getState().live;
    // the last answer stays on screen while the supplier is asked again (a package tick must not blank W5); it is not "live" for the
    // new signature until the answer comes (liveIsCurrent)
    const asking = new RequestStateModel<CruiseLive>(new DataLoadStatusModel('loading'));
    asking.response = before.response ?? null;
    patchState({ live: asking });
    return this.#api.livePrice(toApiDraft(draft)).pipe(
      tap(({ value, traceId }) => {
        const { shown } = getState();
        const moved = compare && shown && shown.signature === signature && shown.total !== value.price.total;
        if (moved) {
          // R-3: nothing is shown as live at another price silently - the agency accepts or declines first
          patchState({
            live: before,
            priceChange: {
              kind: 'live',
              oldPrice: shown.total,
              newPrice: value.price.total,
              currency: value.price.currency,
              traceId,
              pendingLive: value,
              pendingSignature: signature
            }
          });
          return;
        }
        this.#adoptLive(getState, patchState, value, signature);
      }),
      catchError((err: CruiseApiError) => {
        // R-57: an unreachable supplier leaves no price labelled live
        patchState({ live: cruiseErrorState<CruiseLive>(err), liveSignature: null });
        return EMPTY;
      })
    );
  }

  @Action(cruisesAcceptPriceChange)
  acceptPriceChange({ getState, patchState, dispatch }: Ctx) {
    const change = getState().priceChange;
    if (!change) return;
    patchState({ priceChange: null });
    if (change.kind === 'live') {
      if (change.pendingLive && change.pendingSignature)
        this.#adoptLive(getState, patchState, change.pendingLive, change.pendingSignature);
      return;
    }
    if (change.pendingLive && change.pendingSignature)
      this.#adoptLive(getState, patchState, change.pendingLive, change.pendingSignature);
    // P4-9: the new price is booked on the quote the server kept at it - the same reservation, never a second registration
    return dispatch(new cruisesBook(change.kind, change.newPrice));
  }

  @Action(cruisesDeclinePriceChange)
  declinePriceChange({ patchState }: Ctx) {
    patchState({ priceChange: null });
  }

  @Action(cruisesGoTo)
  goTo({ patchState }: Ctx, { step }: cruisesGoTo) {
    patchState({ step });
  }

  @Action(cruisesSetCabin)
  setCabin({ getState, patchState }: Ctx, { cabinNumber }: cruisesSetCabin) {
    const { draft } = getState();
    if (draft) patchState({ draft: { ...draft, cabinNumber } });
  }

  @Action(cruisesSetDining)
  setDining({ getState, patchState }: Ctx, { diningKey }: cruisesSetDining) {
    const { draft } = getState();
    if (draft) patchState({ draft: { ...draft, diningKey } });
  }

  /** R-54: once the reservation is registered its tourists are the guests - the operator changes them, not the website. */
  @Action(cruisesUpdateGuest)
  updateGuest({ getState, patchState }: Ctx, { seq, patch }: cruisesUpdateGuest) {
    const { draft } = getState();
    if (!draft || draft.rfid) return;
    patchState({ draft: { ...draft, guests: draft.guests.map((g) => (g.seq === seq ? { ...g, ...patch } : g)) } });
  }

  @Action(cruisesAddGuest)
  addGuest({ getState, patchState }: Ctx) {
    const { draft } = getState();
    if (!draft || draft.rfid) return;
    const seq = Math.max(0, ...draft.guests.map((g) => g.seq)) + 1;
    patchState({ draft: { ...draft, guests: [...draft.guests, newGuest(seq, null)] } });
  }

  @Action(cruisesRemoveGuest)
  removeGuest({ getState, patchState }: Ctx, { seq }: cruisesRemoveGuest) {
    const { draft } = getState();
    if (!draft || draft.rfid || draft.guests.length <= 1) return;
    patchState({
      draft: {
        ...draft,
        guests: draft.guests.filter((g) => g.seq !== seq),
        packages: draft.packages.filter((p) => p.guestSeq !== seq)
      }
    });
  }

  @Action(cruisesCheckGuests, { cancelUncompleted: true })
  checkGuests({ getState, patchState }: Ctx) {
    const { draft, live, shown } = getState();
    if (!draft?.offerKey) return;
    patchState({ guestCheck: loading<CruiseCheck>() });
    const signature = signatureOf(draft);
    return this.#api.check(toApiDraft(draft), live.response?.price.total ?? shown?.total ?? 0, 'option').pipe(
      tap(({ value }) => {
        patchState({ guestCheck: new RequestStateModel<CruiseCheck>(value) });
        // the check's live answer was made with the TYPED guests: it becomes the current one, so the cart's reasons follow it (05.10.2026: names do not
        // change the price signature, the cart kept the answer of empty names and Book with option stayed disabled after a check that said "ok").
        // A moved price is not adopted silently (R-3) - it goes through the price-change dialog as before.
        const now = getState().draft;
        if (value.live && value.outcome !== 'price_changed' && now && signatureOf(now) === signature)
          this.#adoptLive(getState, patchState, value.live, signature);
      }),
      catchError((err: CruiseApiError) => {
        patchState({ guestCheck: cruiseErrorState<CruiseCheck>(err) });
        return EMPTY;
      })
    );
  }

  @Action(cruisesSetPackage)
  setPackage({ getState, patchState }: Ctx, { packageId, guestSeq, selected }: cruisesSetPackage) {
    const { draft } = getState();
    if (!draft) return;
    const others = draft.packages.filter((p) => !(p.packageId === packageId && p.guestSeq === guestSeq));
    patchState({ draft: { ...draft, packages: selected ? [...others, { packageId, guestSeq }] : others } });
  }

  @Action(cruisesBook, { cancelUncompleted: true })
  book({ getState, patchState }: Ctx, { kind, expectedPrice }: cruisesBook) {
    const { draft, live } = getState();
    const expected = expectedPrice ?? live.response?.price.total ?? null;
    if (!draft?.offerKey || expected === null) return;
    // R-54: a new reservation needs every guest's gender - nothing is asked before it is chosen (the cart says why, C3)
    if (!draft.rfid && guestWithoutGender(draft.guests) !== null) return;
    patchState({ booking: loading<CruiseBookResult>() });
    return this.#api
      .book(kind, {
        draft: toApiDraft(draft),
        expectedPrice: expected,
        rfid: draft.rfid,
        quoteId: draft.quoteId,
        rowVersion: draft.rowVersion
      })
      .pipe(
        tap(({ value, traceId }) => {
          const kept = this.#keepReservation(getState().draft, value.rfid ?? null, value.rfNumber ?? null, value);
          const current = getState();
          if (value.outcome === 'price_changed') {
            patchState({
              draft: kept,
              booking: new RequestStateModel<CruiseBookResult>(),
              priceChange: {
                kind,
                oldPrice: value.oldPrice ?? expected,
                newPrice: value.newPrice ?? value.booking?.total ?? expected,
                currency:
                  value.booking?.currency ?? value.live?.price.currency ?? current.live.response?.price.currency ?? '',
                traceId,
                // moved at the check (nothing registered): accepting shows the new live answer's lines before booking at its total (R-50)
                pendingLive: value.live ?? null,
                pendingSignature: value.live && kept ? signatureOf(kept) : null
              }
            });
            return;
          }
          patchState({ draft: kept, booking: new RequestStateModel<CruiseBookResult>(value), booked: value.booking });
        }),
        catchError((err: CruiseApiError) => {
          // P4-7: after the registration the reservation is kept (and a supplier refusal leaves the cruise as a quote) - Retry books into it
          const kept = this.#keepReservation(getState().draft, err.rfid, err.rfNumber, {
            booking: err.booking,
            touristIds: err.touristIds
          });
          patchState({
            draft: kept,
            booking: cruiseErrorState<CruiseBookResult>(err),
            booked: err.booking ?? getState().booked
          });
          return EMPTY;
        })
      );
  }

  @Action(cruisesLoadCountries, { cancelUncompleted: true })
  loadCountries({ getState, patchState }: Ctx, { refresh }: cruisesLoadCountries) {
    if (getState().countries.loadState.status === 'completed' && !refresh) return;
    patchState({ countries: loading<Country[]>() });
    return this.#catalogue.getCountries().pipe(
      tap((r) => patchState({ countries: new RequestStateModel<Country[]>(r?.countries ?? []) })),
      catchError((e: ApiErrorResponse) => {
        patchState({
          countries: new RequestStateModel<Country[]>(
            new DataLoadStatusModel('error', e?.displayErrorMessage ?? 'The country list could not be loaded', e)
          )
        });
        return EMPTY;
      })
    );
  }

  #adoptLive(getState: () => CruisesStateModel, patchState: Ctx['patchState'], value: CruiseLive, signature: string) {
    const { draft } = getState();
    if (!draft) return;
    // a cabin or sitting the supplier no longer offers is not kept (R-4)
    const cabinNumber =
      draft.cabinNumber && value.cabins.some((c) => c.cabinNumber === draft.cabinNumber) ? draft.cabinNumber : null;
    const diningKey = draft.diningKey && value.dining.some((d) => d.key === draft.diningKey) ? draft.diningKey : null;
    patchState({
      draft: { ...draft, cabinNumber, diningKey },
      live: new RequestStateModel<CruiseLive>(value),
      liveSignature: signature,
      shown: { signature, total: value.price.total, currency: value.price.currency }
    });
  }

  /** The reservation registered for the draft, its tourists' ids and the quote the server kept (P4-7 / P4-9). */
  #keepReservation(
    draft: CruiseWizardDraft | null,
    rfid: string | null,
    rfNumber: string | null,
    result: Partial<Pick<CruiseBookResult, 'touristIds' | 'booking'>>
  ): CruiseWizardDraft | null {
    if (!draft) return draft;
    const ids = result.touristIds ?? [];
    const booking = result.booking ?? null;
    return {
      ...draft,
      rfid: rfid ?? draft.rfid,
      rfNumber: rfNumber ?? booking?.rfNumber ?? draft.rfNumber,
      guests: draft.guests.map((g) => ({ ...g, rftid: ids.find((t) => t.seq === g.seq)?.rftid ?? g.rftid })),
      quoteId: booking && booking.status !== 'option' ? booking.bookingId : draft.quoteId,
      rowVersion: booking?.rowVersion ?? draft.rowVersion
    };
  }
}
